(() => {
  const select = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const queued = new WeakSet();
  const responsiveMedia = new WeakMap();
  const watchedMedia = new Set();
  const pendingMedia = new Set();
  let mediaFrame = 0;
  const mediaSelector = 'img.lazyload,img[data-src],img[data-srcset],[data-bgset],[data-bg],[data-eu-responsive-media]';
  const density = () => Math.min(window.devicePixelRatio || 1, 2);
  const renderedWidth = element => Math.ceil(element.clientWidth || element.parentElement?.clientWidth || window.innerWidth);
  function updateMedia(element, state) {
    if (state.kind === 'background') {
      if (!element.clientWidth) return;
      const target = Math.max(element.clientWidth, 320) * density();
      const candidate = state.candidates.find(item => item.width >= target) || state.candidates.at(-1);
      if (candidate && candidate.width > state.selectedWidth) {
        element.style.backgroundImage = `url(${JSON.stringify(candidate.url)})`;
        state.selectedWidth = candidate.width;
      }
      return;
    }
    const width = renderedWidth(element);
    if (state.autoSizes && width > 0 && element.sizes !== `${width}px`) element.sizes = `${width}px`;
    if (state.sourceTemplate && !element.srcset) {
      const target = Math.ceil(Math.max(width, 320) * density());
      if (target > state.selectedWidth) {
        element.src = state.sourceTemplate.replace('{width}', target);
        state.selectedWidth = target;
      }
    }
  }
  const releaseMedia = element => {
    mediaResize?.unobserve(element);
    watchedMedia.delete(element);
    pendingMedia.delete(element);
  };
  const scheduleMedia = element => {
    if (!responsiveMedia.has(element)) return;
    pendingMedia.add(element);
    if (mediaFrame) return;
    mediaFrame = requestAnimationFrame(() => {
      mediaFrame = 0;
      const entries = [...pendingMedia];
      pendingMedia.clear();
      entries.forEach(media => {
        if (!media.isConnected) releaseMedia(media);
        else updateMedia(media, responsiveMedia.get(media));
      });
    });
  };
  const mediaResize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => entries.forEach(entry => scheduleMedia(entry.target))) : null;
  const watchMedia = (element, state) => {
    queued.add(element);
    element.dataset.euResponsiveMedia = 'true';
    responsiveMedia.set(element, state);
    watchedMedia.add(element);
    mediaResize?.observe(element);
  };
  window.addEventListener('resize', () => watchedMedia.forEach(scheduleMedia), { passive: true });
  function ready(image) {
    image.classList.remove('lazyload', 'lazyloading');
    image.classList.add('lazyloaded');
    image.closest('[data-image-with-placeholder-wrapper],.product-single__photo-wrapper,.grid-view-item__image-wrapper')?.querySelectorAll('[data-image-placeholder],.placeholder-background').forEach(node => node.classList.add('hide'));
  }
  function loadImage(element) {
    if (element.tagName !== 'IMG') {
      const set = element.dataset.bgset;
      const target = Math.max(element.clientWidth, 320) * density();
      const candidates = set ? [...set.matchAll(/([^\s,]+)\s+(\d+)w(?:\s+\d+h)?/g)].map(match => ({ url: match[1], width: Number(match[2]) })).sort((a, b) => a.width - b.width) : [];
      const candidate = candidates.find(item => item.width >= target) || candidates.at(-1);
      const url = candidate?.url || element.dataset.bg || set?.split(/\s/)[0];
      if (url) element.style.backgroundImage = `url(${JSON.stringify(url)})`;
      if (candidates.length > 1) watchMedia(element, { kind: 'background', candidates, selectedWidth: candidate.width });
      delete element.dataset.bgset;
      delete element.dataset.bg;
      ready(element);
      return;
    }
    element.closest('picture')?.querySelectorAll('source[data-srcset]').forEach(source => {
      source.srcset = source.dataset.srcset;
      delete source.dataset.srcset;
    });
    let src = element.dataset.src;
    let sourceTemplate = null;
    let selectedWidth = 0;
    if (src?.includes('{width}')) {
      let widths;
      try { widths = JSON.parse(element.dataset.widths || '[]'); } catch { widths = []; }
      if (widths.length) element.srcset = widths.map(width => `${src.replace('{width}', width)} ${width}w`).join(',');
      if (!widths.length) sourceTemplate = src;
      selectedWidth = Math.ceil(Math.max(renderedWidth(element), 320) * density());
      src = src.replace('{width}', selectedWidth);
    }
    const autoSizes = element.dataset.sizes === 'auto';
    if (autoSizes) element.sizes = `${renderedWidth(element)}px`;
    else if (element.dataset.sizes) element.sizes = element.dataset.sizes;
    if (element.dataset.srcset) element.srcset = element.dataset.srcset;
    element.addEventListener('load', () => ready(element), { once: true });
    element.addEventListener('error', () => element.classList.remove('lazyloading'), { once: true });
    element.classList.add('lazyloading');
    if (src) element.src = src;
    if (autoSizes || sourceTemplate) watchMedia(element, { kind: 'image', autoSizes, sourceTemplate, selectedWidth });
    delete element.dataset.src;
    delete element.dataset.srcset;
    if (element.complete && element.naturalWidth) ready(element);
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting && (entry.target.tagName === 'IMG' || (entry.boundingClientRect.width > 0 && entry.boundingClientRect.height > 0))) {
      observer.unobserve(entry.target);
      loadImage(entry.target);
    }
  }), { rootMargin: '300px' }) : null;
  function replaceTag(element, tag) {
    const replacement = document.createElement(tag);
    [...element.attributes].forEach(attribute => replacement.setAttribute(attribute.name, attribute.value));
    replacement.append(...element.childNodes);
    element.replaceWith(replacement);
    return replacement;
  }
  function prepareRte(root) {
    select(root, '.rte,.qks-rich-text').forEach(rte => {
      const product = Boolean(rte.closest('body.template-product'));
      const article = Boolean(rte.closest('body.template-article'));
      rte.querySelectorAll('img').forEach(image => {
        if (image.dataset.euRtePrepared) return;
        image.dataset.euRtePrepared = 'true';
        let wrap = image.parentElement;
        if (rte.classList.contains('qks-rich-text') && !wrap.matches('a,.rte-image')) {
          wrap = document.createElement(product ? 'a' : 'div');
          image.before(wrap); wrap.append(image);
        }
        if (wrap === rte || !wrap.matches('div,a,.rte-image')) return;
        wrap.classList.add('rte-image');
        const align = image.style.float || wrap.style.textAlign;
        const center = image.style.display === 'block' && image.style.marginLeft === 'auto';
        if (['left','right','center','justify'].includes(align)) wrap.classList.add('rte-image-' + (align === 'justify' ? 'wide' : align));
        if (center) wrap.classList.add('rte-image-center');
        if (product) {
          if (wrap.tagName !== 'A') wrap = replaceTag(wrap, 'a');
          wrap.classList.add('fancybox');
          if (!wrap.hasAttribute('href')) wrap.href = (image.dataset.src || image.currentSrc || image.src).replace('{width}', '2200');
        }
        const quote = wrap.nextElementSibling;
        if (quote?.tagName === 'BLOCKQUOTE') wrap.append(replaceTag(quote, 'figcaption'));
        if (article && image.alt && !wrap.querySelector('figcaption')) {
          const caption = document.createElement('figcaption'); caption.textContent = image.alt; wrap.append(caption);
        }
        if (article && image.alt) wrap.dataset.caption = image.alt;
        image.removeAttribute('style');
      });
      rte.querySelectorAll('pre').forEach(pre => replaceTag(pre, 'p').classList.add('footnote'));
      rte.querySelectorAll('table').forEach(table => {
        if (table.parentElement.classList.contains('table-wrapper')) return;
        const wrap = document.createElement('div'); wrap.className = 'table-wrapper'; table.before(wrap); wrap.append(table);
      });
      rte.querySelectorAll('iframe[src*="youtube"],iframe[src*="vimeo"]').forEach(frame => {
        if (frame.parentElement.classList.contains('video-wrapper')) return;
        const wrap = document.createElement('div'); wrap.className = 'video-wrapper'; frame.before(wrap); wrap.append(frame);
      });
    });
  }
  function init(root = document) {
    prepareRte(root);
    select(root, mediaSelector).forEach(element => {
      if (element.closest('[data-slideshow-deferred]')) return;
      if (queued.has(element)) {
        if (responsiveMedia.has(element)) {
          watchMedia(element, responsiveMedia.get(element));
          scheduleMedia(element);
        }
        return;
      }
      queued.add(element);
      if (element.getAttribute('loading') === 'eager' || !observer) loadImage(element); else observer.observe(element);
    });
    select(root, 'a[href]').forEach(link => {
      if (/^https?:$/.test(link.protocol) && link.hostname !== location.hostname) {
        link.target = '_blank'; link.relList.add('noopener', 'noreferrer');
      }
    });
    if (!document.querySelector('.qks-root')) document.querySelectorAll('.product-single__description').forEach(description => description.style.display = 'block');
  }
  window.QuadroEU = Object.assign(window.QuadroEU || {}, { init, loadImage });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  new MutationObserver(records => {
    if (records.some(record => record.removedNodes.length)) watchedMedia.forEach(element => { if (!element.isConnected) releaseMedia(element); });
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1 && !node.matches('script,style,link')) init(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
